import { knowledgeEngineDataPrefix } from "LeadProbateContextWrapper";
import { ProbateField, ProbateFieldJSON } from "./ProbateField";
import { ProbateTypeJSON, ProbateType } from "./ProbateType";

export type ProbateDetailsJSON = {
  field_categories?: any;
  fields_needed_ids?: string[];
  fields_used?: any;
  qualifying_form_ids?: string[];
  probate_type: ProbateTypeJSON;
  qualifying_probate_description?: string;
  // qualifying_probate_id?: number;
  // qualifying_probate_type?: string;
  // qualifying_probate_type_mapping?: string;
  probate_type_definitions?: Array<unknown>;
};

export type FieldCategory = {
  id: string;
  title: string;
  order: number;
};

export type ProbateFieldCategory = {
  id: string;
  title: string;
  order: number;
  fields?: ProbateField[];
};

export class ProbateDetails {
  constructor(
    public fieldsUsed?: ProbateField[],
    public fieldsNeededIds?: string[],
    public qualifyingFormIds?: string[],
    public probateType?: ProbateType,
    // public qualifyingProbateType?: string,
    // public qualifyingProbateTypeMapping?: string,
    // public qualifyingProbateId?: number,
    public categoriesWithFields?: ProbateFieldCategory[], // sorted categories with fields used by UI
    public qualifyingProbateDescription?: string,
    public probateTypeDefinitions?: Array<unknown> // only on local dev for debugging
  ) {}

  static fromJSON(json: ProbateDetailsJSON): ProbateDetails {
    // get the fields
    const fieldsUsed = (
      Object.values(json.fields_used) as ProbateFieldJSON[]
    )?.map((fieldJson: ProbateFieldJSON) => ProbateField.fromJSON(fieldJson));
    const sortedFieldCategories = this.sortFieldsIntoCategories(
      json.field_categories,
      fieldsUsed
    );
    if (json) {
      return new ProbateDetails(
        fieldsUsed,
        json.fields_needed_ids || undefined,
        json.qualifying_form_ids || undefined,
        json.probate_type
          ? ProbateType.fromJSON(json.probate_type).attachExplanation(
              json.qualifying_probate_description || undefined
            )
          : undefined,
        // json.qualifying_probate_type || undefined,
        // json.qualifying_probate_type_mapping || undefined,
        // json.qualifying_probate_id || undefined,
        sortedFieldCategories,
        json.qualifying_probate_description || undefined,
        json.probate_type_definitions || undefined
      );
    } else {
      return new ProbateDetails();
    }
  }

  static sortFieldsIntoCategories(
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    categories: any,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    fieldsUsed: any
  ): ProbateFieldCategory[] | undefined {
    // get the categories
    const sortedFieldCategories =
      (categories as ProbateFieldCategory[]) || undefined;
    // sort the categories
    sortedFieldCategories?.sort(
      (a: FieldCategory, b: FieldCategory) => a.order - b.order
    );
    // add fields for sortedFieldCategories
    if (sortedFieldCategories) {
      fieldsUsed?.forEach((field: ProbateField) => {
        if (field.category) {
          const categoryIndex = sortedFieldCategories.findIndex(
            (category: ProbateFieldCategory) => category.id === field.category
          );
          if (sortedFieldCategories[categoryIndex]) {
            if (!sortedFieldCategories[categoryIndex].fields) {
              sortedFieldCategories[categoryIndex].fields = [];
            }
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            sortedFieldCategories[categoryIndex].fields.push(field);
          }
        }
      });
    }
    return sortedFieldCategories;
  }

  /**
   * injectPreviouslyAnswered
   *  Fields returned by the api may not include all fields answered by a customer during this session
   *  ProbateContext.fieldsAnswered tracks all fields answered this session
   *
   *  confirm each field in ProbateContext.fieldsAnswered can be found in this.fieldsUsed
   *  if not, inject it into this.categoriesWithFields
   * @param fieldsAnswered
   */
  injectPreviouslyAnswered(fieldsAnswered?: ProbateField[]): void {
    if (fieldsAnswered && this.fieldsUsed && this.categoriesWithFields) {
      fieldsAnswered.forEach((fieldAnswered: ProbateField) => {
        const found = this.fieldsUsed?.find((field: ProbateField) => {
          return field.id === fieldAnswered.id;
        });
        if (!found) this.injectField(fieldAnswered);
      });
    }
  }

  
  /**
   * injectPreviouslyAnsweredFromLeadData
   *  Takes lead data and sets fields to their answer values where applicable
   *
   * @param fieldsAnswered
   */
  injectPreviouslyAnsweredFromLeadData(leadData: {[key:string]: any}): void {
    const categoriesWithFields = this.categoriesWithFields
    const newCategoriesWithFields:ProbateFieldCategory[] = []
    categoriesWithFields?.map((c:ProbateFieldCategory) => {
      const newCategory = c
      if(newCategory.fields) {
        for(let i = 0; i < newCategory.fields.length; i++) {
          const leadDataKey = knowledgeEngineDataPrefix + newCategory.fields[i].id
          if((knowledgeEngineDataPrefix + newCategory.fields[i].id) == leadDataKey && leadData[leadDataKey]) {
            const leadDataField = typeof leadData[leadDataKey] === "object" ? leadData[leadDataKey] : JSON.parse(leadData[leadDataKey])
            newCategory.fields[i].answerValue = leadDataField.answerValue
          }
        }
      }
      newCategoriesWithFields.push(newCategory)
    })
    this.categoriesWithFields = newCategoriesWithFields
  }

  /**
   * inject a field cached on the front-end into the api response
   * @param fieldAnswered
   */
  injectField(fieldAnswered: ProbateField): void {
    const categoryId = fieldAnswered.category;
    const categoryIndex = this.categoriesWithFields?.findIndex(
      (category: ProbateFieldCategory) => {
        return category.id === categoryId;
      }
    );
    // console.log("injecting previously answered field: ", fieldAnswered);
    if (categoryIndex !== undefined && this.categoriesWithFields) {
      if (!this.categoriesWithFields[categoryIndex]?.fields) {
        this.categoriesWithFields[categoryIndex].fields = [];
      }
      this.categoriesWithFields[categoryIndex]?.fields?.push(fieldAnswered);
    }
  }

  getNextField(skipFields?: string[]): ProbateField | undefined {
    let nextField: ProbateField | undefined = undefined;
    if (this.fieldsNeededIds?.length) {
      this.fieldsNeededIds.some((fieldId: string) => {
        if (!skipFields?.includes(fieldId)) {
          // this field hasn't been skipped
          nextField = this.fieldsUsed?.find((field: ProbateField) => {
            return field.id === fieldId;
          });
          return true; // found a non-skipped field
        }
        return false; // keep checking for a field not in skipFields
      });
    }
    return nextField;
  }
}
